import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  MessageSquare, Search, Trash2, Eye, Mail, Phone, User, 
  Loader2, Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContacts = () => {
  const { getAuthAxios, fetchDashboard } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const interestLabels = {
    participation: "Participating in the run",
    volunteering: "Volunteering",
    sponsorship: "Sponsorship opportunities",
    partnership: "Partnership/Collaboration",
    media: "Media/Press inquiry",
    other: "Other"
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchTerm]);

  const fetchContacts = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/contacts`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let items = [...contacts];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(c => 
        c.first_name?.toLowerCase().includes(term) ||
        c.last_name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.message?.toLowerCase().includes(term)
      );
    }
    
    setFilteredContacts(items);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      const authAxios = getAuthAxios();
      await authAxios.delete(`${API}/admin/contacts/${id}`);
      toast.success("Message deleted");
      fetchContacts();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", { 
      day: "numeric", 
      month: "short", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Contact Messages</h1>
        <p className="text-[#4B5563]">View and manage contact form submissions</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input 
            placeholder="Search by name, email, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Messages */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
        </div>
      ) : filteredContacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Messages Found</h3>
            <p className="text-[#4B5563]">
              {searchTerm ? "Try adjusting your search" : "No contact messages yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredContacts.map((contact) => (
            <Card key={contact.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#0A0A0A]">
                          {contact.first_name} {contact.last_name}
                        </h3>
                        <span className="px-2 py-0.5 bg-[#FAFAFA] rounded-full text-xs text-[#4B5563]">
                          {interestLabels[contact.interest] || contact.interest}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#4B5563] mb-3">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {contact.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(contact.created_at)}
                        </span>
                      </div>
                      <p className="text-[#4B5563] line-clamp-2">{contact.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedContact(contact); setViewDialogOpen(true); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <a href={`mailto:${contact.email}`}>
                      <Button variant="outline" size="sm">
                        <Mail className="w-4 h-4" />
                      </Button>
                    </a>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(contact.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Contact Message</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedContact.first_name} {selectedContact.last_name}</h3>
                  <p className="text-[#4B5563]">{interestLabels[selectedContact.interest]}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#4B5563]">Email</p>
                  <a href={`mailto:${selectedContact.email}`} className="text-[#007AFF] hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Phone</p>
                  <a href={`tel:${selectedContact.phone}`} className="text-[#007AFF] hover:underline">
                    {selectedContact.phone}
                  </a>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-[#4B5563] mb-2">Message</p>
                <div className="bg-[#FAFAFA] p-4 rounded-xl">
                  <p className="text-[#0A0A0A] whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>
              
              <div className="text-xs text-[#4B5563]">
                Received: {formatDate(selectedContact.created_at)}
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <a href={`mailto:${selectedContact.email}`} className="flex-1">
                  <Button className="w-full btn-primary">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply via Email
                  </Button>
                </a>
                <a href={`tel:${selectedContact.phone}`}>
                  <Button variant="outline">
                    <Phone className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContacts;
